import {
  objectBasePort,
  objectFactoryId,
} from "../../../layout/scene-object-types.js";
import {
  DataSourceId,
  InjectedData,
  getSourceValue,
  resolvePath,
} from "../../../layout/index.js";
import { BindingExpression, VariableNode } from "../../types.js";
import { ExecutionContext, NodeExecutionResult } from "../types.js";
import { emptyPatchResult } from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

const LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE = "layoutListRowItem";
const LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE = "layoutListComparator";

export class VariableBehaviorNode extends BehaviorNode {
  declare behavior: VariableNode;
  constructor(behavior: VariableNode) {
    super(behavior);
  }
  execute(_context: ExecutionContext): NodeExecutionResult {
    return {
      patch: emptyPatchResult(),
      nextFlows: [],
      scheduledNodes: [],
      runtimeSignals: [],
    };
  }
  getOutput(
    portId: string,
    _visiting: Set<string>,
    context: ExecutionContext,
    binding?: boolean,
  ): unknown {
    const extensionPort = objectBasePort(this.behavior.objectSource);
    if (extensionPort) {
      const frame =
        context.variableInputFrameStack?.[
          context.variableInputFrameStack.length - 1
        ];
      const typeId =
        this.behavior.ports.find((p) => p.id === portId)?.objectTypeId ??
        this.behavior.ports.find(
          (p) => p.direction === "output" && p.objectTypeId,
        )?.objectTypeId;
      const source = typeId?.startsWith("source:")
        ? typeId.slice(7)
        : undefined;
      const supplied = frame?.inputs[extensionPort];
      const fallback = binding
        ? source
          ? { kind: "binding", path: source }
          : { kind: "literal", value: undefined }
        : source
          ? (context.data as unknown as Record<string, unknown>)[source]
          : undefined;
      let value =
        binding && supplied
          ? {
              kind: "ternary",
              operator: "ifElse",
              input1: supplied,
              input2: fallback,
              input3: {
                kind: "binary",
                operator: "notEquals",
                input1: supplied,
                input2: { kind: "literal", value: undefined },
              },
            }
          : (supplied ?? fallback);
      if (typeId && !source) {
        const id = objectFactoryId(typeId);
        const entry = context.variableRuntimeEntries?.get(id);
        const stack = context.variableEvalStack ?? [];
        if (
          entry &&
          stack.length < 32 &&
          !stack.some((f) => f.variableId === id)
        ) {
          const end = [...entry.runtime.nodes.values()].find(
            (n) =>
              n.behavior.type === "control" &&
              n.behavior.controlType === "variableEnd",
          );
          value = end?.getInput(
            "value",
            new Set(),
            {
              ...context,
              variableEvalStack: [
                ...stack,
                { variableId: id, refNodeId: this.behavior.id },
              ],
              variableInputFrameStack: [
                ...(context.variableInputFrameStack ?? []),
                { inputs: { base: value } },
              ],
            },
            binding,
          );
        }
      }
      if (!this.behavior.objectFieldPath) return value;
      return binding
        ? {
            kind: "unary",
            operator: "get",
            input: value,
            options: { objectFieldPath: this.behavior.objectFieldPath },
          }
        : resolvePath(value, this.behavior.objectFieldPath);
    }
    if (binding) {
      if (this.behavior.objectSource) {
        if (
          this.behavior.objectSource === LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE
        ) {
          const scopeKey =
            portId === "outputB" ? "iterationItemB" : "iterationItemA";
          const path = this.behavior.objectFieldPath
            ? `${scopeKey}.${this.behavior.objectFieldPath}`
            : scopeKey;
          return { kind: "binding", path } as BindingExpression;
        }
        return {
          kind: "binding",
          path:
            this.behavior.objectSource +
            (this.behavior.objectFieldPath
              ? "." + this.behavior.objectFieldPath
              : ""),
        } as BindingExpression;
      }
      const exactPort = this.behavior.ports.find((port) => port.id === portId);
      return {
        kind: "literal",
        value: exactPort?.value,
      } as BindingExpression;
    }
    if (this.behavior.objectSource) {
      if (this.behavior.objectSource === "barLoaderDisks") {
        const exactPort = this.behavior.ports.find(
          (port) => port.id === portId,
        );
        return exactPort?.value;
      }

      if (this.behavior.objectSource === "currentNode") {
        const roundedX = Math.round(Number(context.currentNodeContext?.x ?? 0));
        const roundedY = Math.round(Number(context.currentNodeContext?.y ?? 0));
        const roundedWidth = Math.round(
          Number(context.currentNodeContext?.width ?? 0),
        );
        const roundedHeight = Math.round(
          Number(context.currentNodeContext?.height ?? 0),
        );
        const roundedRotation = Math.round(
          Number(context.currentNodeContext?.rotation ?? 0),
        );
        const roundedSkewX = Math.round(
          Number(context.currentNodeContext?.skewX ?? 0),
        );
        const roundedSkewY = Math.round(
          Number(context.currentNodeContext?.skewY ?? 0),
        );
        const safeX = Number.isFinite(roundedX) ? roundedX : 0;
        const safeY = Number.isFinite(roundedY) ? roundedY : 0;
        const safeWidth = Number.isFinite(roundedWidth) ? roundedWidth : 0;
        const safeHeight = Number.isFinite(roundedHeight) ? roundedHeight : 0;
        const safeRotation = Number.isFinite(roundedRotation)
          ? roundedRotation
          : 0;
        const safeSkewX = Number.isFinite(roundedSkewX) ? roundedSkewX : 0;
        const safeSkewY = Number.isFinite(roundedSkewY) ? roundedSkewY : 0;

        if (this.behavior.objectFieldPath === "x") {
          return safeX;
        }
        if (this.behavior.objectFieldPath === "y") {
          return safeY;
        }

        if (this.behavior.objectFieldPath === "width") {
          return safeWidth;
        }
        if (this.behavior.objectFieldPath === "height") {
          return safeHeight;
        }
        if (this.behavior.objectFieldPath === "rotation") {
          return safeRotation;
        }
        if (this.behavior.objectFieldPath === "skewX") {
          return safeSkewX;
        }
        if (this.behavior.objectFieldPath === "skewY") {
          return safeSkewY;
        }
        return {
          x: safeX,
          y: safeY,
          width: safeWidth,
          height: safeHeight,
          rotation: safeRotation,
          skewX: safeSkewX,
          skewY: safeSkewY,
        };
      }

      if (this.behavior.objectSource === "rowIndex") {
        const rowIndex = Number(context.data.rowIndex ?? 0);
        return Number.isFinite(rowIndex) ? Math.round(rowIndex) : 0;
      }

      if (this.behavior.objectSource === "sortAIndex") {
        const sortAIndex = Number(
          (context.data as InjectedData & { sortAIndex?: number }).sortAIndex ??
            0,
        );
        return Number.isFinite(sortAIndex) ? Math.round(sortAIndex) : 0;
      }

      if (this.behavior.objectSource === "sortBIndex") {
        const sortBIndex = Number(
          (context.data as InjectedData & { sortBIndex?: number }).sortBIndex ??
            0,
        );
        return Number.isFinite(sortBIndex) ? Math.round(sortBIndex) : 0;
      }

      if (this.behavior.objectSource === LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE) {
        // Prefer row object injected by `LayoutList` (`buildRowData`).
        // `BehaviorRuntime` does not populate `context.dataBinding`, so the
        // legacy array+rowIndex path alone could not resolve the current row.
        const injectedRowItem = context.data.layoutListRowItem;
        if (injectedRowItem != null) {
          if (this.behavior.objectFieldPath) {
            return resolvePath(injectedRowItem, this.behavior.objectFieldPath);
          }
          return injectedRowItem;
        }
        const sourceId = context.dataBinding?.path.split(".")[0] ?? "";
        const source = context.data[sourceId as DataSourceId];
        if (this.behavior.objectFieldPath && Array.isArray(source)) {
          return resolvePath(
            source[context.data.rowIndex ?? 0],
            this.behavior.objectFieldPath,
          );
        } else if (Array.isArray(source)) {
          return source[context.data.rowIndex ?? 0];
        }
        return source;
      }

      if (this.behavior.objectSource === LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE) {
        const sourceId = context.dataBinding?.path.split(".")[0] ?? "";
        const source = context.data[sourceId as DataSourceId];
        const rowIndex = Number(
          portId === "outputB"
            ? ((context.data as InjectedData & { sortBIndex?: number })
                .sortBIndex ?? 0)
            : ((context.data as InjectedData & { sortAIndex?: number })
                .sortAIndex ?? 0),
        );
        if (this.behavior.objectFieldPath && Array.isArray(source)) {
          return resolvePath(
            source[Math.max(0, Math.round(rowIndex))],
            this.behavior.objectFieldPath,
          );
        } else if (Array.isArray(source)) {
          return source[Math.max(0, Math.round(rowIndex))];
        }
        return source;
      }

      const source = getSourceValue(
        context.data,
        this.behavior.objectSource as DataSourceId,
      );
      if (this.behavior.objectFieldPath) {
        return resolvePath(source, this.behavior.objectFieldPath);
      }
      return source;
    }

    const exactPort = this.behavior.ports.find((port) => port.id === portId);
    return exactPort?.value;
  }
}
